function rosterIds(list) {
  return (list || []).filter(Boolean);
}

function splitPlayerName(player) {
  const first = (player?.first_name || '').trim();
  const last = (player?.last_name || '').trim();
  if (first || last) return { first_name: first, last_name: last };
  const parts = String(player?.name || '').trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || 'Unknown',
    last_name: parts.slice(1).join(' '),
  };
}

function playerRow(playerId, playersById) {
  const player = playersById?.[playerId];
  const names = splitPlayerName(player);
  return {
    id: playerId,
    first_name: names.first_name,
    last_name: names.last_name,
    gender: player?.gender || null,
    is_guest: Boolean(player?.is_guest),
    is_active: player?.is_active !== false,
    merged_at: player?.merged_at ?? null,
    guest_merge_declined_at: player?.guest_merge_declined_at ?? null,
  };
}

function scoreFromHistory(entry) {
  const score = entry?.score;
  if (score && typeof score === 'object') {
    return {
      team1: Number(score.team1Score ?? score.team_1_score) || 0,
      team2: Number(score.team2Score ?? score.team_2_score) || 0,
    };
  }
  if (typeof score === 'string' && score.includes('-')) {
    const [left, right] = score.split('-');
    return { team1: Number(left) || 0, team2: Number(right) || 0 };
  }
  return { team1: 0, team2: 0 };
}

function winningTeamFromHistory(entry, team1Score, team2Score) {
  if (entry?.result === 'team1' || entry?.winningTeam === 'team1' || entry?.winning_team === 1) return 1;
  if (entry?.result === 'team2' || entry?.winningTeam === 'team2' || entry?.winning_team === 2) return 2;
  if (team1Score > team2Score) return 1;
  if (team2Score > team1Score) return 2;
  return null;
}

function gameFingerprint(game) {
  const rows = game?.players_session_games || [];
  const team1 = rows.filter((row) => row.team_number === 1).map((row) => row.player_id).sort().join(',');
  const team2 = rows.filter((row) => row.team_number === 2).map((row) => row.player_id).sort().join(',');
  return `${team1}|${team2}|${game?.team_1_score ?? 0}-${game?.team_2_score ?? 0}`;
}

/**
 * Map live matchHistory (+ playersById) into the session_games shape used by Games / Ranking tabs.
 */
export function buildLocalSessionGamesFromMatchHistory(matchHistory, playersById = {}, sessionId = null) {
  return (matchHistory || []).map((entry, index) => {
    const team1 = rosterIds(entry.team1 || entry.teams?.[0]);
    const team2 = rosterIds(entry.team2 || entry.teams?.[1]);
    if (team1.length === 0 && team2.length === 0) return null;
    const { team1: team1Score, team2: team2Score } = scoreFromHistory(entry);
    const winningTeam = winningTeamFromHistory(entry, team1Score, team2Score);
    const createdAt = entry.timestamp || entry.created_at || new Date().toISOString();
    const id = entry.gameId || entry.id || `local:${createdAt}:${index}`;
    const pendingSync = String(id).startsWith('local:');

    const players_session_games = [
      ...team1.map((playerId) => ({
        player_id: playerId,
        team_number: 1,
        result: winningTeam === 1 ? 'win' : winningTeam === 2 ? 'loss' : null,
        pair_type: null,
        players: playerRow(playerId, playersById),
      })),
      ...team2.map((playerId) => ({
        player_id: playerId,
        team_number: 2,
        result: winningTeam === 2 ? 'win' : winningTeam === 1 ? 'loss' : null,
        pair_type: null,
        players: playerRow(playerId, playersById),
      })),
    ];

    return {
      id,
      session_id: sessionId,
      team_1_score: team1Score,
      team_2_score: team2Score,
      winning_team: winningTeam,
      start_time: createdAt,
      end_time: createdAt,
      created_at: createdAt,
      game_type_id: null,
      game_types: null,
      players_session_games,
      elo_history: [],
      pendingSync,
    };
  }).filter(Boolean);
}

/** Keep server rows, then append local-only games that are not already on the server. */
export function mergeLocalSessionGames(serverGames = [], localGames = []) {
  const serverIds = new Set((serverGames || []).map((game) => game?.id).filter(Boolean));
  const serverKeys = new Set((serverGames || []).map(gameFingerprint));
  const extras = (localGames || []).filter((game) => {
    if (game?.id && serverIds.has(game.id)) return false;
    return !serverKeys.has(gameFingerprint(game));
  });
  return [...(serverGames || []), ...extras];
}

function matchHistoryFingerprint(entry) {
  const team1 = rosterIds(entry?.team1 || entry?.teams?.[0]).slice().sort().join(',');
  const team2 = rosterIds(entry?.team2 || entry?.teams?.[1]).slice().sort().join(',');
  const score = entry?.score;
  const team1Score = typeof score === 'object'
    ? (score.team1Score ?? score.team_1_score ?? '')
    : '';
  const team2Score = typeof score === 'object'
    ? (score.team2Score ?? score.team_2_score ?? '')
    : '';
  return `${team1}|${team2}|${team1Score}-${team2Score}`;
}

/** Keep server match history, then append local-only finished games. */
export function mergeLocalMatchHistory(serverHistory = [], localHistory = []) {
  const keys = new Set((serverHistory || []).map(matchHistoryFingerprint));
  const ids = new Set(
    (serverHistory || []).map((entry) => entry?.gameId || entry?.id).filter(Boolean)
  );
  const extras = (localHistory || []).filter((entry) => {
    const id = entry?.gameId || entry?.id;
    if (id && ids.has(id)) return false;
    return !keys.has(matchHistoryFingerprint(entry));
  });
  return [...(serverHistory || []), ...extras];
}
